import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { removeScripts } from '@utils/helper';
import { Collapse } from 'react-bootstrap';

interface Props {
  detail: string;
  showTOC?: boolean;
}

const generateSlug = text => {
  const diacriticsMap = {
    đ: 'd',
    Đ: 'D',
  };

  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, match => diacriticsMap[match] || match)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();
};

const TableOfContent = memo(({ detail, showTOC }: Props) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  const contentConverted = useMemo(() => {
    let content = removeScripts(detail);
    const arr = content?.match(/<(h[1-6])(.+?)<\/\1>/gi);
    if (arr && content) {
      content = arr.reduce((result, item) => {
        const rest = result.split(item);

        const doc = new DOMParser().parseFromString(
          item.replace(/&nbsp;/gm, ' ').replace(/<br>/g, ' '),
          'text/xml',
        );
        const newNode = doc.firstChild as HTMLElement;
        const headingText = newNode.textContent || '';
        const headingSlug = generateSlug(headingText);
        newNode.id = headingSlug;
        doc.firstChild.replaceWith(newNode);
        const heading = newNode.outerHTML;
        if (heading) {
          if (rest.length > 1) {
            let arrPop = [...rest.slice(0, 0), ...rest.slice(0 + 1)];
            arrPop = [
              ...arrPop.slice(0, arrPop.length - 1),
              ...arrPop.slice(arrPop.length),
            ];
            const allRestEnd = arrPop.reduce((res, el) => res + el + item, '');
            result = rest[0] + heading + allRestEnd + rest[rest.length - 1];
          } else if (rest.length === 1) {
            result = rest[0] + heading;
          }
        }
        return result;
      }, content);
    }

    return content;
  }, [detail]);

  const renderHeading = useCallback(value => {
    const paddingClass = /<h1[^>]*>/.test(value)
      ? 'pH1'
      : /<h2[^>]*>/.test(value)
      ? 'pH2'
      : /<h3[^>]*>/.test(value)
      ? 'pH3'
      : /<h4[^>]*>/.test(value)
      ? 'pH4'
      : /<h5[^>]*>/.test(value)
      ? 'pH5'
      : /<h6[^>]*>/.test(value)
      ? 'pH6'
      : '';

    let html = value.replace(/&nbsp;/gm, ' ');
    html = html.replace(/<br>/g, ' ');
    const doc = new DOMParser().parseFromString(html, 'text/xml');
    const headingText = doc.firstChild.textContent || '';
    const headingSlug = generateSlug(headingText);

    return (
      <a
        href={`#${headingSlug}`}
        key={headingSlug}
        className={`--item ${paddingClass}`}
        // onClick={() => {
        //   const target = document.getElementById(headingSlug);
        //   target &&
        //     animate(
        //       document.scrollingElement || document.documentElement,
        //       'scrollTop',
        //       '',
        //       0,
        //       target.offsetTop,
        //       300,
        //       true,
        //     );
        // }}
      >
        <span className="mb-0">{headingText}</span>
      </a>
    );
  }, []);

  const getHeadingItem = useCallback(() => {
    if (detail) {
      const arr = detail.match(/<(h[1-6])(.+?)<\/\1>/gi);
      if (arr) {
        const arr_value = arr.map(val => renderHeading(val));
        if (arr_value.length)
          return (
            <div className="--card-title">
              <ul className="qs-lv-1">{arr_value}</ul>
            </div>
          );
      }
    }
    return <div />;
  }, [detail, renderHeading]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;

    const handleScroll = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0;
      setIsInView(isVisible);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMounted]);

  if (!hasMounted) return null;

  return (
    <>
      {showTOC && (
        <>
          <div ref={cardRef} className="card_news">
            <div className="--top">
              <div className="--titleCard">Menu</div>
              <button
                onClick={() => setOpen(!open)}
                aria-controls="example-collapse-text"
                aria-expanded={open}
              >
                <i className="fa-solid fa-bars" />
              </button>
            </div>
            <Collapse in={open}>
              <div id="example-collapse-text">{getHeadingItem()}</div>
            </Collapse>
          </div>

          {!isInView && (
            <div className={`card_news post_card fixed-card`}>
              <div className="--top">
                <button
                  onClick={() => setOpen(!open)}
                  aria-controls="example-collapse-text"
                  aria-expanded={open}
                >
                  <i className="fa-solid fa-bars" />
                </button>
              </div>
              <Collapse in={open}>
                <div id="example-collapse-text">{getHeadingItem()}</div>
              </Collapse>
            </div>
          )}
        </>
      )}
      <article
        dangerouslySetInnerHTML={{
          __html: removeScripts(contentConverted ? contentConverted : null),
        }}
      />
    </>
  );
});

export default TableOfContent;
